<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import {
    proposalActions,
    proposalCardModel,
  } from '@/domain/menu-import/assistant-view';
  import type { TProposalFormat } from '@/domain/menu-import/proposal-effects';
  import { listJoin } from '@/domain/store-setup/display-names';
  import { formatMoney } from '@/domain/store-setup/money';
  import type {
    IMenuDraft,
    IMenuImportTurn,
    IMenuProposal,
  } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * One assistant proposal (plan §4.6, D18): «Propuesta · {count} cambios»,
   * the assistant's summary, the change rows behind «Ver cambios», and
   * «Aplicar» / «Descartar» / «Probar otra vez». Nothing applies by itself;
   * a stale proposal only offers «Probar otra vez». Once applied, discarded
   * or stale the card keeps its summary and state only (no count, no rows).
   */
  const props = defineProps<{
    turn: IMenuImportTurn;
    proposal: IMenuProposal;
    draft: IMenuDraft;
    currency: string;
    country: string;
    /** An action of the chat is being saved. */
    busy: boolean;
    /** «Probar otra vez» sends a new message: not while another one waits or offline. */
    retryDisabled: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'apply'): void;
    (e: 'discard'): void;
    (e: 'retry'): void;
    /** «Ver cambios» opened the change rows. */
    (e: 'show-changes'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const listId = nextDomId('menu-proposal-changes');

  const expanded = ref(false);

  const format = computed<TProposalFormat>(() => {
    const locale = proxy.$i18n.locale;
    return {
      money: (value) => formatMoney(value, props.currency, props.country),
      list: (items) => listJoin(items, locale),
    };
  });
  const card = computed(() =>
    proposalCardModel(props.proposal, props.draft, format.value)
  );
  const actions = computed(() => proposalActions(props.proposal.status));
  /** The count while pending, the badge once applied or discarded (a stale card has neither). */
  const hasHead = computed(
    () =>
      card.value.count !== null ||
      props.proposal.status === 'applied' ||
      props.proposal.status === 'discarded'
  );
  const hasActions = computed(
    () => actions.value.apply || actions.value.discard || actions.value.retry
  );

  function toggleChanges() {
    expanded.value = !expanded.value;
    if (expanded.value) emit('show-changes');
  }
</script>

<template>
  <section class="v2-menu-proposal-card" :class="`is-${proposal.status}`">
    <header v-if="hasHead" class="card-head">
      <span v-if="card.count !== null" class="card-label">{{
        $tc('menuImport.proposal.label', card.count, { count: card.count })
      }}</span>
      <PosUiBadge
        v-if="proposal.status === 'applied'"
        color="success"
        size="sm"
        icon="check"
        >{{ $t('menuImport.proposal.applied') }}</PosUiBadge
      >
      <PosUiBadge v-else-if="proposal.status === 'discarded'" size="sm">{{
        $t('menuImport.proposal.discarded')
      }}</PosUiBadge>
    </header>
    <p class="card-summary">{{ proposal.summary }}</p>
    <p v-if="proposal.status === 'stale'" class="card-stale" role="status">
      <PosUiIcon name="alert-circle-outline" class="stale-icon" />
      <span>{{ $t('menuImport.proposal.stale') }}</span>
    </p>
    <button
      v-if="card.lines.length > 0"
      type="button"
      class="changes-toggle"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-controls="listId"
      @click="toggleChanges"
    >
      <span>{{
        expanded
          ? $t('menuImport.proposal.hideChanges')
          : $t('menuImport.proposal.showChanges')
      }}</span>
      <PosUiIcon
        :name="expanded ? 'chevron-up' : 'chevron-down'"
        class="toggle-icon"
      />
    </button>
    <ul
      v-show="expanded && card.lines.length > 0"
      :id="listId"
      class="change-list"
    >
      <li v-for="(line, index) in card.lines" :key="index" class="change-row">
        {{ tr(line.key, line.params) }}
      </li>
      <li v-if="card.more > 0" class="change-more">
        {{ $t('menuImport.proposal.more', { count: card.more }) }}
      </li>
    </ul>
    <div v-if="hasActions" class="card-actions">
      <PosUiButton
        v-if="actions.apply"
        color="success"
        variant="soft"
        :disabled="busy"
        @click="emit('apply')"
        >{{ $t('menuImport.proposal.apply') }}</PosUiButton
      >
      <PosUiButton
        v-if="actions.discard"
        color="neutral"
        variant="outline"
        :disabled="busy"
        @click="emit('discard')"
        >{{ $t('menuImport.proposal.discard') }}</PosUiButton
      >
      <PosUiButton
        v-if="actions.retry"
        color="neutral"
        variant="ghost"
        :disabled="busy || retryDisabled"
        @click="emit('retry')"
        >{{ $t('menuImport.proposal.retry') }}</PosUiButton
      >
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-proposal-card {
    @apply tw-flex tw-flex-col tw-gap-2.5 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-3;
    &.is-applied {
      @apply tw-border-emerald-200 tw-bg-emerald-50;
    }
    &.is-discarded {
      @apply tw-bg-neutral-50;
      .card-summary {
        @apply tw-text-neutral-500;
      }
    }
    .card-head {
      @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2;
    }
    .card-label {
      @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .card-summary {
      @apply tw-m-0 tw-break-words tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .card-stale {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-1.5 tw-text-sm tw-text-neutral-500;
      .stale-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
      }
    }
    .changes-toggle {
      @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-1 tw-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      .toggle-icon {
        @apply tw-text-lg tw-no-underline;
      }
    }
    .change-list {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-rounded-lg tw-bg-neutral-50 tw-p-2.5 tw-text-sm tw-tabular-nums tw-text-neutral-900;
    }
    .change-row {
      @apply tw-break-words;
    }
    .change-more {
      @apply tw-text-neutral-500;
    }
    .card-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
  }
</style>
